import { message } from 'antd';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { endpoints, fetcher } from 'services';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getAccessToken, saveTokens } from 'utils/token-utils';
import useStore from 'store';
import { MSG } from 'commons/constants';
import Paths from 'routes';

const useAuth = () => {
  /* ======================================================
   * state
   * ====================================================== */
  const [loginUser, setLoginUser] = useState<LoginInReqDto>({ email: '', password: '' });
  const [time, setTime] = useState<moment.Moment>();

  /* ======================================================
   * store
   * ====================================================== */
  const auth = useStore((state) => state.auth);
  const reset = useStore((state) => state.reset);

  /* ======================================================
   * hooks
   * ====================================================== */
  const navigate = useNavigate();

  const { isLoading } = useQuery(
    [endpoints.auth.login, loginUser, time],
    () => fetcher.auth.postLoginIn({ email: loginUser.email, password: loginUser.password }),
    {
      enabled: !!loginUser.email && !!loginUser.password ,
      onSuccess: (res: TokenResDto) => {
        switch (res.code) {
          case 200:
            if (res.result) {
              const { accessToken, refreshToken } = res.result;
              saveTokens(accessToken, refreshToken.refreshToken, refreshToken.refreshTokenExpirationAt, 'master');
              auth.setMyInfo(
                {
                  id: res.result.userid,
                  name: res.result.username,
                  email: res.result.useremail,
                  div: res.result.userdiv,
                  isPm: res.result.isPm,
                  isCm: res.result.isCm,
                  isOp: res.result.isOp,
                }
              );
              if (res.result.isCm || res.result.isPm || res.result.isOp || res.result.userdiv === '101') {
                navigate('/myroom');
              } else {
                navigate('/schedule');
              }
              
            }
            break;
          case 120:
            // 계정 잠김
            message.error(MSG.ERROR_LOGIN_BANNED, 1);
            break;
          case 121:
            // 활성화 되어 있지 않음
            message.error(MSG.ERROR_LOGIN_ACTIVATED, 1);
            break;            
          case 400:
          default:
            message.error(MSG.ERROR_IDPW, 1);
            break;
        }
      },
    },
  );

  useQuery([endpoints.mypage.logininfo], () => fetcher.mypage.getMyLoginInfo(), {
    onSuccess: (res) => {
      switch (res.code) {
        case 200:
          auth.setMyInfo({
            id: res?.result?.userid,
            name: res?.result?.username,
            email: res?.result?.useremail,
            div: res?.result?.userdiv,
            isPm: res?.result?.isPm,
            isCm: res?.result?.isCm,
            isOp: res?.result?.isOp,
          });
          break;
        default:
          break;
      }
    },
    enabled:
      !!getAccessToken() && !isLoading && !auth.login,
  });

  /* ======================================================
   * function
   * ====================================================== */
  const login = (val: LoginInReqDto) => {
    setLoginUser(val);
    setTime(moment());
  };

  const {mutate: register } = useMutation(
    [endpoints.auth.register],
    fetcher.auth.postRegister,
    {
      onSuccess: (res) => {
        switch (res.code) {
          case 200:
            message.info(MSG.SUCCESS_REGISTER_USER, 1);

            navigate('/auth/login');
            break;
          case 122:
            message.error(MSG.ERROR_DUPLICATED_USER, 1);
            break;            
          default:
            message.error(MSG.ERROR_COMMON, 1);
            break;
        }
      },
    },
  )

  const {mutate: activate } = useMutation(
    [endpoints.auth.activate],
    fetcher.auth.postActivate,
    {
      onSuccess: (res) => {
        switch (res.code) {
          case 200:
            message.info(MSG.SUCCESS_ACTIVATE_USER, 1);
            break;
          case 120:
              // 계정 잠김
              message.error(MSG.ERROR_LOGIN_BANNED, 1);
              break;            
          default:
            message.error(MSG.ERROR_ACTIVATE_USER, 1);
            break;
        }
        navigate('/auth/login');        
      },
    },
  )  

  const {mutate: forgotPassword, isLoading: isLoadingForgot } = useMutation(
    [endpoints.auth.forgotpassowrd],
    fetcher.auth.postForgotPassword,
    {
      onSuccess: (res) => {
        switch (res.code) {
          case 200:
            message.info(MSG.SUCCESS_MAIL_SEND, 1);
            break;
          case 120:
              // 계정 잠김
              message.error(MSG.ERROR_LOGIN_BANNED, 1);
              break;            
          default:
            message.error(MSG.ERROR_COMMON, 1);
            break;
        }
        navigate('/auth/login');        
      },
    },
  )    

  const {mutate: resetPassword, isLoading: isLoadingReset } = useMutation(
    [endpoints.auth.resetpassword],
    fetcher.auth.postResetPassword,
    {
      onSuccess: (res) => {
        switch (res.code) {
          case 200:
            message.info(MSG.SUCCESS_RESET_PASSWORD, 1);
            break;
          case 120:
              // 계정 잠김
              message.error(MSG.ERROR_LOGIN_BANNED, 1);
              break;            
          default:
            message.error(MSG.ERROR_ACTIVATE_USER, 1);
            break;
        }
        navigate('/auth/login');        
      },
    },
  )  

  return { login, register, activate, forgotPassword, resetPassword, isLoading, isLoadingForgot, isLoadingReset };
};

export default useAuth;
